import type { CategoryTreeNode } from '../../lib/categoryTree.ts';
import type { CategoryColour, LibraryItem } from '../../../shared/api.ts';

/**
 * The grouped body: which headings the Library shows, in what order, and which
 * items sit under each.
 *
 * Grouping by category is the Library's DEFAULT view (`group: 'category'` in
 * `libraryFilterState.ts`), which is the difference between this page and the
 * decks grid: a deck is looked for by name, an item is browsed for by the kind
 * of thing it is. Group = None renders the flat grid instead and never comes
 * through here.
 *
 * Three decisions worth the words.
 *
 * **A group is one exact category path, never a subtree.** The rail's rows
 * count a parent plus its children, because picking a parent there is a filter
 * that means "and everything under it". A heading cannot do both: an item filed
 * in `Case studies/Acme` appearing under `Case studies` AND under
 * `Case studies › Acme` is the same card printed twice on one screen. So the
 * tree is flattened to one heading per node, each holding only what is filed
 * exactly there.
 *
 * **An empty category keeps its heading, but only on an unnarrowed page.** That
 * is what the design's per-group "Save a slide here" call to action is for — a
 * category someone made and has not filled yet is the whole reason the registry
 * carries empty paths at all. The moment something IS narrowing the body,
 * every category that does not match would come back as an empty heading, so a
 * search for one word would draw twelve of them. Same rule, and the same
 * reasoning, as the decks page's recent strip: the extra furniture is for a
 * page nobody is searching.
 *
 * **Uncategorised comes last and only when it has something in it.** Library
 * items always carry a category, so the bucket is empty by construction; it is
 * still built properly, because an item saved without one would otherwise
 * vanish from a body that claims to show everything.
 */

export type LibraryGroup = {
  /** The exact category path, or null for the Uncategorised bucket. Also the
   *  React key and the `data-group` the UI check names a heading by. */
  path: string | null;
  /** What the heading reads: the leaf for a child, with its parent in front. */
  label: string;
  /** The registry tint, when the category has one — the design's coloured dot
   *  on the heading. The Library's registry carries colours as of Run 11 (the
   *  decks page's Run 0 repeated for this shelf), so an untinted category is a
   *  choice now rather than a gap. */
  colour: CategoryColour | null;
  items: LibraryItem[];
};

type LibraryGroupsInput = {
  /** Already filtered AND ordered — the grouping preserves that order inside
   *  each heading, so Sort keeps working exactly as it does in the flat grid. */
  items: readonly LibraryItem[];
  /** The rail's tree, which carries the registry's display order and every
   *  category including the empty ones. */
  tree: readonly CategoryTreeNode[];
  /** True while any axis is narrowing the body — `filtering` from
   *  `useLibraryFilters`. Empty headings are dropped when it is. */
  filtering: boolean;
};

/** The heading for a path: a child says which parent it belongs to, since a
 *  heading has no indentation to say it for it. */
export function groupLabel(path: string): string {
  return path.replace('/', ' › ');
}

export function libraryGroups({ items, tree, filtering }: LibraryGroupsInput): LibraryGroup[] {
  const byPath = new Map<string, LibraryItem[]>();
  const uncategorised: LibraryItem[] = [];
  for (const item of items) {
    const path = (item.category || '').trim();
    if (!path) {
      uncategorised.push(item);
      continue;
    }
    const key = path.toLowerCase();
    const bucket = byPath.get(key);
    if (bucket) bucket.push(item);
    else byPath.set(key, [item]);
  }

  const groups: LibraryGroup[] = [];
  const take = (node: { path: string; colour?: CategoryColour | null }) => {
    const held = byPath.get(node.path.toLowerCase()) ?? [];
    if (held.length === 0 && filtering) return;
    groups.push({ path: node.path, label: groupLabel(node.path), colour: node.colour ?? null, items: held });
    byPath.delete(node.path.toLowerCase());
  };
  for (const parent of tree) {
    take(parent);
    for (const child of parent.children) take(child);
  }

  // A path carried by an item but missing from the tree cannot happen while
  // the tree is built from those same items — but it would be a card that
  // silently disappeared if it ever did, so anything left over gets a heading
  // of its own rather than being dropped.
  for (const [, held] of byPath) {
    const path = (held[0]?.category || '').trim();
    groups.push({ path, label: groupLabel(path), colour: null, items: held });
  }

  if (uncategorised.length > 0) {
    groups.push({ path: null, label: 'Uncategorised', colour: null, items: uncategorised });
  }
  return groups;
}

/** "4 items" — the number under a heading, which is what it counts and nothing
 *  more. */
export function groupCountLine(n: number): string {
  return `${n} ${n === 1 ? 'item' : 'items'}`;
}
